import React, { useMemo } from 'react';
import { useLanguage } from '../../i18n';
import { PILLAR_COLORS, PILLAR_ORDER, toPoints } from '../../utils/energyPalette';

// ─────────────────────────────────────────────────────────────────────────
// PizzaChart — Level 1. A hand-rolled inline-SVG donut: this repo ships NO
// charting library and one is not worth adding for five slices.
//
// Slices are `<path fill=…>` annular sectors rather than dashed circle strokes,
// so each slice's colour genuinely lives in a `fill` attribute (the locked
// palette) and is directly assertable. Angles are computed from the POINT
// values, not the BE's `pct`, so the ring always closes exactly at 360° even
// when the percentages are rounded.
//
// All five pillars ALWAYS appear in the legend — a zero pillar is information
// ("you spent nothing on reasoning"), not an absence — but a zero pillar draws
// no arc and is not clickable (there is nothing to drill into).
// ─────────────────────────────────────────────────────────────────────────

const CX = 80;
const CY = 80;
const R_OUTER = 74;
const R_INNER = 46;
const FULL_CIRCLE_EPSILON = 359.999;

const round = (n) => Math.round(n * 1000) / 1000;

const polar = (r, deg) => {
    // -90 so 0° is 12 o'clock and slices run clockwise.
    const rad = ((deg - 90) * Math.PI) / 180;
    return [round(CX + r * Math.cos(rad)), round(CY + r * Math.sin(rad))];
};

export function annularSectorPath(startDeg, endDeg, rOuter = R_OUTER, rInner = R_INNER) {
    const large = endDeg - startDeg > 180 ? 1 : 0;
    const [x1, y1] = polar(rOuter, startDeg);
    const [x2, y2] = polar(rOuter, endDeg);
    const [x3, y3] = polar(rInner, endDeg);
    const [x4, y4] = polar(rInner, startDeg);
    return `M ${x1} ${y1} A ${rOuter} ${rOuter} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${rInner} ${rInner} 0 ${large} 0 ${x4} ${y4} Z`;
}

// A 360° sector degenerates (start point === end point), so a lone pillar owning
// 100% is drawn as a true ring: two circles with fill-rule evenodd.
export function fullRingPath(rOuter = R_OUTER, rInner = R_INNER) {
    const ring = (r) =>
        `M ${CX - r} ${CY} A ${r} ${r} 0 1 0 ${CX + r} ${CY} A ${r} ${r} 0 1 0 ${CX - r} ${CY} Z`;
    return `${ring(rOuter)} ${ring(rInner)}`;
}

const PizzaChart = ({ data, onSelectPillar, stacked = false }) => {
    const { t } = useLanguage();

    const { rows, arcs, sum } = useMemo(() => {
        const byName = new Map((data?.pillars || []).map((p) => [p?.pillar, p]));
        const built = PILLAR_ORDER.map((pillar) => {
            const row = byName.get(pillar);
            return {
                pillar,
                // The BE's 2dp string, rendered verbatim. '0.00' is the honest
                // stand-in when a pillar is missing from the payload entirely.
                pts: row?.pts ?? '0.00',
                value: Math.max(0, toPoints(row?.pts)),
                rawPct: row?.pct,
            };
        });
        const total = built.reduce((acc, r) => acc + r.value, 0);

        const shapes = [];
        let angle = 0;
        for (const r of built) {
            if (total <= 0 || r.value <= 0) continue;
            const sweep = (r.value / total) * 360;
            shapes.push({
                pillar: r.pillar,
                d: sweep >= FULL_CIRCLE_EPSILON ? fullRingPath() : annularSectorPath(angle, angle + sweep),
                full: sweep >= FULL_CIRCLE_EPSILON,
            });
            angle += sweep;
        }
        return { rows: built, arcs: shapes, sum: total };
    }, [data]);

    const pctLabel = (row) => {
        const raw = Number(row.rawPct);
        const p = Number.isFinite(raw) ? raw : (sum > 0 ? (row.value / sum) * 100 : 0);
        return `${p.toFixed(1)}%`;
    };

    const pillarName = (pillar) =>
        t(`energyPillar_${pillar}`, pillar.charAt(0).toUpperCase() + pillar.slice(1));

    const activate = (pillar, value) => {
        if (value <= 0) return;
        onSelectPillar?.(pillar);
    };

    return (
        <div
            data-testid="energy-pizza"
            className={`flex ${stacked ? 'flex-col items-center' : 'flex-row items-center'} gap-4 min-w-0 max-w-full`}
        >
            {/* The donut. The slices carry the LOCKED pillar palette in a literal
                `fill`; separation between them is bought with a hairline stroke,
                never by shifting a fill. Over a translucent card there is no solid
                ground to borrow, so the stroke is a low-alpha white rather than
                `currentColor`. */}
            <div className="w-40 h-40 flex-shrink-0">
                <svg viewBox="0 0 160 160" width="100%" height="100%" role="img" aria-label={t('energyPizzaLabel', 'Energy by pillar')}>
                    <circle
                        cx={CX}
                        cy={CY}
                        r={(R_OUTER + R_INNER) / 2}
                        fill="none"
                        strokeWidth={R_OUTER - R_INNER}
                        stroke="rgb(255 255 255 / 0.08)"
                    />
                    {arcs.map(({ pillar, d, full }) => (
                        <path
                            key={pillar}
                            data-testid={`energy-slice-${pillar}`}
                            d={d}
                            fill={PILLAR_COLORS[pillar]}
                            fillRule={full ? 'evenodd' : undefined}
                            stroke="rgb(11 15 20 / 0.55)"
                            strokeWidth={1.5}
                            role="button"
                            tabIndex={0}
                            aria-label={pillarName(pillar)}
                            className="cursor-pointer focus:outline-none"
                            onClick={() => onSelectPillar?.(pillar)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onSelectPillar?.(pillar);
                                }
                            }}
                        />
                    ))}
                    <text
                        x={CX}
                        y={CY - 4}
                        textAnchor="middle"
                        fill="rgb(255 255 255 / 0.45)"
                        style={{ fontSize: 9 }}
                    >
                        {t('energyTotalPoints', 'Total points')}
                    </text>
                    <text
                        x={CX}
                        y={CY + 13}
                        textAnchor="middle"
                        data-testid="energy-pizza-total"
                        fill="#ffffff"
                        style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.02em' }}
                    >
                        {/* Verbatim 2dp string from the BE. */}
                        {data?.total_pts ?? '0.00'}
                    </text>
                </svg>
            </div>

            <ul className={`min-w-0 ${stacked ? 'w-full' : 'flex-1'} space-y-1`}>
                {rows.map((row) => {
                    const zero = row.value <= 0;
                    return (
                        <li key={row.pillar} className="min-w-0">
                            <button
                                type="button"
                                data-testid={`energy-legend-${row.pillar}`}
                                data-zero={zero ? 'true' : 'false'}
                                disabled={zero}
                                onClick={() => activate(row.pillar, row.value)}
                                className={`pai-tap-row w-full flex items-center gap-[11px] px-2 py-1.5 rounded-seg text-left min-w-0 max-w-full ${
                                    zero
                                        ? 'opacity-40 cursor-default'
                                        : 'hover:bg-white/[0.07] transition-colors'
                                }`}
                            >
                                <span
                                    aria-hidden="true"
                                    className="w-[11px] h-[11px] rounded-[3px] flex-shrink-0"
                                    style={{ background: PILLAR_COLORS[row.pillar] }}
                                />
                                <span className="text-[13.5px] font-medium text-pai-secondary truncate min-w-0 flex-1">
                                    {pillarName(row.pillar)}
                                </span>
                                <span
                                    data-testid={`energy-legend-pts-${row.pillar}`}
                                    className="text-[13.5px] font-bold text-white tnum flex-shrink-0"
                                >
                                    {row.pts}
                                </span>
                                <span className="text-[12.5px] text-pai-meta tnum flex-shrink-0 w-12 text-right">
                                    {pctLabel(row)}
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default PizzaChart;
